import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useCallback } from "react";
import type { MovementCategory } from "@vlab-corporation/vlab-pilates-service-interface/movement";
import { getMovements, searchMovements } from "@/services/firestore/movements";

export function useMovements(ownerUid: string, centerId: number) {
  const [category, setCategory] = useState<MovementCategory | null>(null);
  const [keyword, setKeyword] = useState("");

  const {
    data: allMovements,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["movements", centerId],
    queryFn: () => getMovements(ownerUid, centerId),
    staleTime: 1000 * 60 * 30,
  });

  const filteredMovements = useMemo(() => {
    let result = allMovements ?? [];
    if (category) {
      result = result.filter((m) => m.category === category);
    }
    if (keyword.trim()) {
      result = searchMovements(result, keyword);
    }
    return result;
  }, [allMovements, category, keyword]);

  const groupedMovements = useMemo(() => {
    const groups = new Map<MovementCategory, typeof filteredMovements>();
    for (const m of filteredMovements) {
      const list = groups.get(m.category) ?? [];
      list.push(m);
      groups.set(m.category, list);
    }
    return groups;
  }, [filteredMovements]);

  const clearFilters = useCallback(() => {
    setCategory(null);
    setKeyword("");
  }, []);

  return {
    movements: filteredMovements,
    groupedMovements,
    isLoading,
    error,
    refetch,
    category,
    setCategory,
    keyword,
    setKeyword,
    clearFilters,
  };
}
